// src/hooks/api/useNations.ts

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { nationsService, type NationFilters } from "@/api/services/nations.service";
import type {
  CreateNationRequest,
  UpdateNationRequest,
  EliminateNationRequest,
} from "@/types/api.types";

// Query Keys
export const nationKeys = {
  all: ["nations"] as const,
  lists: () => [...nationKeys.all, "list"] as const,
  list: (filters?: NationFilters) => [...nationKeys.lists(), filters] as const,
  detail: (id: number) => [...nationKeys.all, id] as const,
};

// Get all nations
export function useNationsAdmin(filters?: NationFilters) {
  return useQuery({
    queryKey: nationKeys.list(filters),
    queryFn: () => nationsService.getAllNations(filters),
    refetchInterval: 60000,
  });
}

// Get single nation
export function useNation(id: number) {
  return useQuery({
    queryKey: nationKeys.detail(id),
    queryFn: () => nationsService.getNation(id),
    enabled: !!id,
  });
}

// Create nation
export function useCreateNation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNationRequest) =>
      nationsService.createNation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nationKeys.lists() });
    },
  });
}

// Update nation
export function useUpdateNation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateNationRequest }) =>
      nationsService.updateNation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nationKeys.lists() });
    },
  });
}

// Delete nation
export function useDeleteNation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => nationsService.deleteNation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nationKeys.lists() });
    },
  });
}

// Eliminate nation
export function useEliminateNation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: EliminateNationRequest }) =>
      nationsService.eliminateNation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nationKeys.lists() });
    },
  });
}
